// eslint-disable-next-line @typescript-eslint/no-use-before-define
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import { Box, Flex, Tooltip, InputGroup, InputRightElement, Spinner, Button, Text, Image } from '@chakra-ui/core'
import { useSelector } from 'react-redux'
import isSolid from 'is-solid'
import { ReactComponent as MicrophoneIcon } from 'assets/microphone-icon.svg'
import paperClipImg from 'assets/paper-clip.png'
import { DropzoneWrapper } from 'components/DropzoneWrapper'
import { useDropzoneHandlers } from 'hooks/useDropzoneHandlers'

import colors from 'constants/colors'
import strings from 'constants/strings'
import { ReactComponent as CameraIcon } from 'assets/icon-capture.svg'
import { ReactComponent as IconSend } from 'assets/icon-send.svg'
import useLocaleText from 'components/useLocaleText'
import LocaleText from 'components/LocaleText'
import { AudioRecorder } from 'components/AudioRecorder'
import { PreviewFile } from 'config/types'
import { isRtlSelector } from 'redux/selectors/general'
import { CustomFieldWrapper, CustomText, CustomTextArea } from 'components/helperStyles'
import { InputTooltip } from './InputTooltip'
import { AudioRecordingButton } from './AudioRecordingButton'
import { isValidDomain } from 'utils'
import * as linkify from 'linkifyjs'
import { LinkPreview } from 'components/LinkPreview'
import MediaModal from 'components/CommonComponents/MediaModal'
import HiddenFileInput from 'components/TasksV2/UI/HiddenInut'

type EnhancedPreviewFile = PreviewFile & {
  forceFileType?: boolean
}

const isVideoFile = (mimeType: string): boolean => {
  return mimeType.startsWith('video/')
}

const isImageFile = (mimeType: string): boolean => {
  return mimeType.startsWith('image/')
}

type Media = EnhancedPreviewFile[]

interface IProps {
  postID: string
  handleCreateComment: (data: IHandleCreateComment) => Promise<void>
  sendCommentLoading: boolean
  isFocused: boolean
  replyCID?: string
  userTag?: string
  closeCallback?: () => void
  isReply?: boolean
  isChat?: boolean
  inputBgColor?: string
  initialCommentText?: string
}

interface IHandleCreateComment {
  comment: string
  images?: PreviewFile[]
  audios?: PreviewFile
  files?: PreviewFile[]
  videos?: PreviewFile[]
}

const CommentInput = ({
  postID,
  handleCreateComment,
  sendCommentLoading,
  userTag,
  isFocused = false,
  closeCallback,
  isReply,
  isChat = false,
  inputBgColor,
  initialCommentText,
}: IProps) => {
  // Refs
  const fileFormRef = useRef<HTMLFormElement | null>(null)
  const commentInputRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // State hooks
  const [isRecording, setIsRecording] = useState(false)
  const [commentText, setCommentText] = useState<string>(initialCommentText || '')
  const [images, setImages] = useState<Media>([])
  const [videos, setVideos] = useState<Media>([])
  const [files, setFiles] = useState<Media>([])
  const [isFormValid, setIsFormValid] = useState<boolean>(false)
  const [mediaModalConfig, setMediaModalConfig] = useState<{
    isOpen: boolean
    isCameraMode: boolean
  }>({
    isOpen: false,
    isCameraMode: false,
  })

  const isRtl = useSelector(isRtlSelector)

  // Localization
  const t_write_a_comment = useLocaleText('write_a_comment')
  const attachFileLiteral = useLocaleText('post_attach_file_web_hover')
  const attachPhotoLiteral = useLocaleText('post_attach_photo_web_hover')
  const attachRecordLiteral = useLocaleText('post_attach_record_web_hover')

  // Constants
  const TEXTAREA_ID = postID + userTag
  const foundLinks = linkify.find(commentText)
  const validLinks = foundLinks.filter((link) => isValidDomain(link.href))

  // Form handlers
  const checkForm = useCallback((): boolean => {
    let isValid = !!commentText.length
    return isValid
  }, [commentText])

  // Effects
  useEffect(() => {
    setIsFormValid(checkForm())
  }, [checkForm])

  useEffect(() => {
    if (isFocused) {
      if (commentInputRef.current) {
        commentInputRef.current.setSelectionRange(
          commentInputRef.current.value.length,
          commentInputRef.current.value.length
        )
        commentInputRef.current.click()
      }
    }
  }, [isFocused])

  // Form handlers
  const onSubmit = async (e?: any, audioLink?: PreviewFile) => {
    if (e) {
      e.preventDefault()
    }
    if (!checkForm() && !audioLink) return
    await handleCreateComment({ comment: commentText, audios: audioLink })
    setImages([])
    setFiles([])
    setCommentText('')

    if (commentInputRef.current) commentInputRef.current.blur()

    if (closeCallback) {
      closeCallback()
    }
  }

  const onEnter = (e: React.KeyboardEvent) => {
    if (document.activeElement && document.activeElement.id === TEXTAREA_ID && e.key === 'Enter' && !e.shiftKey) {
      onSubmit()
      return true
    }
  }

  const onResizeTextareaHandler = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCommentText(e.target.value)
  }

  const onChangeHeightHandler = () => {
    if (!commentText && commentInputRef.current) {
      commentInputRef.current.style.height = '40px'
    }
  }

  // File handlers
  const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      // Обрабатываем все выбранные файлы как обычные файлы, даже если это изображения
      const files: EnhancedPreviewFile[] = Array.from(e.target.files).map((file) => ({
        file,
        previewUrl: URL.createObjectURL(file),
        // Добавляем флаг, что этот файл должен обрабатываться как файл, даже если это картинка
        forceFileType: true,
      }))
      setFiles(files)
      openMediaModal(false)
      fileFormRef.current?.reset()
    }
  }

  const attachFileHandler = () => {
    fileInputRef.current?.click()
  }

  // Audio handlers
  const stopRecording = () => setIsRecording(false)
  const startRecording = () => setIsRecording(true)
  const sendRecord = async (audioLink: PreviewFile) => {
    if (audioLink) {
      onSubmit(null, audioLink)
    }
    setIsRecording(false)
  }

  // Modal handlers
  const openMediaModal = (isCameraMode: boolean) => {
    setMediaModalConfig({
      isOpen: true,
      isCameraMode,
    })
  }

  const closeMediaModal = () => {
    setMediaModalConfig((prev) => ({
      ...prev,
      isOpen: false,
    }))
    // Очищаем файлы только если закрываем без отправки
    if (!mediaModalConfig.isCameraMode) {
      setImages([])
      setFiles([])
    }
  }

  // Media handlers
  const handleUploadMedia = async (files: File[]) => {
    if (!files.length) return

    const previewFiles: EnhancedPreviewFile[] = files.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
      forceFileType: false,
    }))

    const imageFiles: EnhancedPreviewFile[] = []
    const videoFiles: EnhancedPreviewFile[] = []
    const otherFiles: EnhancedPreviewFile[] = []

    previewFiles.forEach((file) => {
      if (isImageFile(file.file.type)) {
        imageFiles.push(file)
      } else if (isVideoFile(file.file.type)) {
        videoFiles.push(file)
      } else {
        otherFiles.push(file)
      }
    })

    if (imageFiles.length > 0) {
      setImages(imageFiles)
    }
    if (videoFiles.length > 0) {
      setVideos(videoFiles)
    }
    if (otherFiles.length > 0) {
      setFiles(otherFiles)
    }
    openMediaModal(false)
  }

  const handleMediaModalSubmit = async ({ commentText, data }: { commentText?: string; data: PreviewFile[] }) => {
    const images: EnhancedPreviewFile[] = []
    const videos: EnhancedPreviewFile[] = []
    let files: EnhancedPreviewFile[] = []

    data.forEach((item) => {
      const enhancedItem = item as EnhancedPreviewFile
      if (isImageFile(enhancedItem.file.type) && !enhancedItem.forceFileType) {
        images.push(enhancedItem)
      } else if (isVideoFile(enhancedItem.file.type) && !enhancedItem.forceFileType) {
        videos.push(enhancedItem)
      } else {
        files.push(enhancedItem)
      }
    })

    const createCommentParams: IHandleCreateComment = {
      comment: commentText || '',
    }

    if (images.length > 0) {
      createCommentParams.images = images
    }
    if (videos.length > 0) {
      createCommentParams.videos = videos
    }
    if (files.length > 0) {
      createCommentParams.files = files
    }
    setCommentText('')
    closeMediaModal()

    await handleCreateComment(createCommentParams)
  }

  useEffect(() => {
    setCommentText(initialCommentText || '')
  }, [initialCommentText])

  const handleRemoveFile = (index: number) => {
    const allFiles = [...images, ...files]
    const fileToRemove = allFiles[index]

    if (fileToRemove.file.type.startsWith('image') || fileToRemove.file.type.startsWith('video')) {
      setImages((prev) => prev.filter((file) => file !== fileToRemove))
    } else {
      setFiles((prev) => prev.filter((file) => file !== fileToRemove))
    }
  }

  const { onDrop, handlePaste } = useDropzoneHandlers({
    onUploadMedia: handleUploadMedia,
    accept: `${strings.POST_FILE_ACCEPT_EXTENSIONS},image/*,video/*`,
  })

  return (
    <Box w="100%">
      {!!validLinks && validLinks.length > 0 && (
        <LinkPreview width="100%" url={validLinks[validLinks.length - 1].href} />
      )}
      <Flex mt="10px" mb={isChat ? '10px' : '5px'} alignItems="center">
        <Flex width={isReply ? '75%' : '100%'} minW="290px" flexDirection="column">
          <DropzoneWrapper onDrop={onDrop} onPaste={handlePaste}>
            <Flex width="100%" style={{ columnGap: '10px' }}>
              {!isRecording && (
                <CustomFieldWrapper flexWrap="wrap" flex={1}>
                  <InputGroup size="sm" flex={1}>
                    {isSolid(userTag) && (
                      <CustomText
                        position="absolute"
                        top={-18}
                        left={isRtl ? 'auto' : 2}
                        right={isRtl ? 2 : 'auto'}
                        fontSize="xs"
                      >
                        <LocaleText text="reply_to_title" />{' '}
                        <Text as="span" fontWeight={700} mb="10px">
                          {userTag}
                        </Text>
                      </CustomText>
                    )}
                    <CustomTextArea
                      placeholder={t_write_a_comment}
                      name="comment"
                      id={TEXTAREA_ID}
                      value={commentText}
                      disabled={sendCommentLoading || isRecording}
                      ref={commentInputRef}
                      onChange={onResizeTextareaHandler}
                      onKeyPress={onEnter}
                      onHeightChange={onChangeHeightHandler}
                      maxRows={5}
                      height="40px"
                      style={{
                        paddingRight: isRtl ? '8px' : '85px',
                        paddingLeft: isRtl ? '85px' : '8px',
                        background: inputBgColor ? inputBgColor : colors.greyMedium,
                      }}
                    />
                    <InputRightElement
                      width="5rem"
                      top="5px"
                      right={isRtl ? 'auto' : '5px'}
                      left={isRtl ? '5px' : 'auto'}
                    >
                      <InputTooltip
                        tooltipText={attachPhotoLiteral}
                        onClick={(e) => {
                          e.preventDefault()
                          openMediaModal(true)
                        }}
                      >
                        <CameraIcon />
                      </InputTooltip>
                      <InputTooltip tooltipText={attachFileLiteral} onClick={attachFileHandler}>
                        <Image src={paperClipImg} />
                      </InputTooltip>
                    </InputRightElement>
                  </InputGroup>
                </CustomFieldWrapper>
              )}
              {isFormValid ? (
                <Button
                  onClick={onSubmit}
                  bg={sendCommentLoading ? 'transparent' : colors.greenLight2}
                  minWidth="auto"
                  isDisabled={sendCommentLoading}
                  width="40px"
                  height="40px"
                  cursor="pointer"
                  justifyContent="center"
                  alignItems="center"
                  borderRadius="50%"
                  p="0"
                  type="submit"
                >
                  {sendCommentLoading ? (
                    <Spinner size="sm" color={colors.greyDark} />
                  ) : (
                    <IconSend width="25px" height="25px" />
                  )}
                </Button>
              ) : isRecording ? (
                <AudioRecorder isRecording={isRecording} onSuccess={sendRecord} onStopRecording={stopRecording} />
              ) : (
                <Tooltip
                  aria-label={attachRecordLiteral}
                  label={attachRecordLiteral}
                  zIndex={100}
                  fontSize="14px"
                  p="10px"
                  placement="top"
                  closeOnClick
                >
                  <AudioRecordingButton onClick={startRecording} isLoading={sendCommentLoading}>
                    <MicrophoneIcon width="25px" height="25px" />
                  </AudioRecordingButton>
                </Tooltip>
              )}
            </Flex>
          </DropzoneWrapper>
        </Flex>
      </Flex>
      <form ref={fileFormRef}>
        <HiddenFileInput
          multiple={true}
          accept={strings.POST_FILE_ACCEPT_EXTENSIONS}
          ref={fileInputRef}
          onChange={handleFileInput}
        />
      </form>
      {mediaModalConfig.isOpen && (
        <MediaModal
          isOpen={mediaModalConfig.isOpen}
          onClose={closeMediaModal}
          onSubmit={handleMediaModalSubmit}
          isLoading={sendCommentLoading}
          onRemove={handleRemoveFile}
          initialPreview={mediaModalConfig.isCameraMode ? [] : [...images, ...videos, ...files]}
          isCameraMode={mediaModalConfig.isCameraMode}
          initialCommentText={commentText}
        />
      )}
    </Box>
  )
}

export default CommentInput
